import { useEffect, useMemo, useState } from 'react'

import type { Range } from '../commonTypes'

const useFreeEntryRange = ({
  selected,
  onChange,
  desktopScreen,
}: {
  /**
   * The value that is currently applied
   */
  selected: Range

  /**
   * Callback to apply a new range
   */
  onChange: (value: Range) => void

  /**
   * Is this the "desktop" version of the filter?
   *
   * Desktop doesn't apply immediately, and should update what
   * is shown if something else causes the applied value to
   * change (e.g. Saved Search).
   *
   * Mobile applies immediately (but to an intermediate
   * state managed by the MobileSearchFilters components),
   * and should ignore any background changes to applied
   * filters (though none are expected to happen in practice).
   */
  desktopScreen: boolean
}) => {
  const [freeEntryRange, setFreeEntryRange] = useState<Range>(selected)

  useEffect(() => {
    if (desktopScreen) {
      setFreeEntryRange(selected) // oxlint-disable-line react/set-state-in-effect
    }
  }, [selected, desktopScreen])

  const { setMin, setMax } = useMemo(() => {
    const onChangeIfNotDesktop = (updated: Range) => {
      if (!desktopScreen) {
        onChange(updated)
      }
      return updated
    }

    return {
      setMin: (min: number | null) =>
        setFreeEntryRange(([_oldMin, max]) => onChangeIfNotDesktop([min, max])),
      setMax: (max: number | null) =>
        setFreeEntryRange(([min, _oldMax]) => onChangeIfNotDesktop([min, max])),
    }
  }, [desktopScreen, onChange])

  return {
    freeEntryRange,
    setMin,
    setMax,
  }
}

export default useFreeEntryRange
